/**
 * Walks the intake graph. Pure functions over the workspace, so the UI (and the
 * future AI assistant) can drive it the same way and the tests can walk every
 * path.
 */

import { NODE_BY_ID, NODES, TYPICAL_TERM, type Answer, type IntakeNode } from './graph.js';
import { fact, newId, nowIso, type ChatMessage, type Workspace } from '../workspace/schema.js';

export function applies(node: IntakeNode, ws: Workspace): boolean {
  return node.when ? node.when(ws) : true;
}

/** The next unanswered, unskipped question that applies; null when done. */
export function nextNode(ws: Workspace): IntakeNode | null {
  const done = new Set([...ws.intake.answered, ...ws.intake.skipped]);
  return NODES.find((n) => !done.has(n.id) && applies(n, ws)) ?? null;
}

export function isComplete(ws: Workspace): boolean {
  return nextNode(ws) === null;
}

/** Share of applicable questions answered or skipped. */
export function progress(ws: Workspace): { done: number; total: number } {
  const applicable = NODES.filter((n) => applies(n, ws));
  const done = new Set([...ws.intake.answered, ...ws.intake.skipped]);
  return { done: applicable.filter((n) => done.has(n.id)).length, total: applicable.length };
}

function msg(role: ChatMessage['role'], text: string, nodeId?: string): ChatMessage {
  return { id: newId('m'), role, text, ...(nodeId ? { nodeId } : {}), at: nowIso() };
}

function withoutNode(list: string[], id: string): string[] {
  return list.filter((x) => x !== id);
}

/** Record an answer: update the profile, mark the node done, extend the conversation. */
export function answer(ws: Workspace, nodeId: string, a: Answer): Workspace {
  const node = NODE_BY_ID.get(nodeId);
  if (!node) throw new Error(`Unknown intake question: ${nodeId}`);
  let next = node.apply(ws, a);
  const transcript = [
    ...next.intake.transcript,
    msg('assistant', node.prompt(ws), nodeId),
    msg('user', node.echo(a, next) + (a.kind === 'number' && a.note ? ` (${a.note})` : ''), nodeId),
  ];
  const note = node.after?.(next, a);
  if (note) transcript.push(msg('assistant', note, nodeId));
  next = {
    ...next,
    intake: {
      answered: [...withoutNode(next.intake.answered, nodeId), nodeId],
      skipped: withoutNode(next.intake.skipped, nodeId),
      transcript,
    },
  };
  // Every estimate needs a term. Until the owner picks one, start from the
  // typical term for their goal; the term question can change it.
  if (nodeId === 'goal' && !next.profile.termMonths && next.profile.goal) {
    next = { ...next, profile: { ...next.profile, termMonths: fact(TYPICAL_TERM[next.profile.goal.value]) } };
  }
  return next;
}

export function skip(ws: Workspace, nodeId: string): Workspace {
  const node = NODE_BY_ID.get(nodeId);
  if (!node) throw new Error(`Unknown intake question: ${nodeId}`);
  return {
    ...ws,
    intake: {
      answered: withoutNode(ws.intake.answered, nodeId),
      skipped: [...withoutNode(ws.intake.skipped, nodeId), nodeId],
      transcript: [
        ...ws.intake.transcript,
        msg('assistant', node.prompt(ws), nodeId),
        msg('user', 'Skip for now', nodeId),
      ],
    },
  };
}

/** Ask a question again (to change an answer). Other answers are kept. */
export function reopen(ws: Workspace, nodeId: string): Workspace {
  return {
    ...ws,
    intake: {
      ...ws.intake,
      answered: withoutNode(ws.intake.answered, nodeId),
      skipped: withoutNode(ws.intake.skipped, nodeId),
    },
  };
}

/** Start the conversation over, keeping documents and scenarios. */
export function restartIntake(ws: Workspace): Workspace {
  return { ...ws, intake: { answered: [], skipped: [], transcript: [] } };
}
